import { isBundleIdOfApple } from "helpers";
import isEqual from "lodash.isequal";
import { ICampaignServer } from "model/campaigns";
import { AndOrListV3 } from "../../lineitem";
import { IDefaultCampaignIdRange } from "./defaultCampaignId";
import { DefaultNetwork } from "./options";
import { TSkadBlockConfig } from "./skadBlock";
import { SkanVersion } from "./skanVersion";

export const MIN_SKAD_ID_LOW = 1;
export const MAX_SKAD_ID_LOW = 40;
export const MIN_SKAD_ID_HIGH = 41;
export const MAX_SKAD_ID_HIGH = 80;

export type ISkadNetworkStrategy = string;
export type ISkadNetworkTemplate = {
  templateId: string;
  type: ISkadNetworkStrategy;
  networkName: string;
  networkId: string;
  __templateId: string;
  minSkadId?: typeof MIN_SKAD_ID_LOW | typeof MIN_SKAD_ID_HIGH;
  maxSkadId?: typeof MAX_SKAD_ID_LOW | typeof MAX_SKAD_ID_HIGH;
};
export type TCreativeSkanId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export const MAX_CREATIVE_SKAD_INDEX = 9;
export type TSkanCreativeData = {
  creativeId: string;
  skanId?: TCreativeSkanId;
  dateAdded?: number;
};
export type ISkAdNetworkConfiguration = {
  templateId?: string;
  defaultCampaignId?: IDefaultCampaignIdRange;
  enabled?: boolean;
  targetSkanVersion?: SkanVersion;
  createdAt?: number;
  disabledTimestamp?: number;
  networkId?: string;
  defaultNetworkId?: DefaultNetwork; //related to defaultCampaignId

  skadBlockConfig?: TSkadBlockConfig;

  creativeData?: TSkanCreativeData[];
};
export type PreviousSkAdNetworkConfiguration = ISkAdNetworkConfiguration & {
  placements?: Record<string, unknown>;
  bundlesV2?: AndOrListV3;
  creativeIds?: string[];
  postbackConversionValues?: any;
};

export interface IArchiveLineItemToRecycle {
  liId: string;
  defaultCampaignId?: IDefaultCampaignIdRange;
  name: string;
  archivedDate?: Date;
  defaultNetworkId?: DefaultNetwork;
}

export const canBeSkadTracked = (campaign: Partial<ICampaignServer>) =>
  isBundleIdOfApple(campaign?.bundleId);
// NOTE: if existing line item doesn't have skAdNetworkConfiguration it is not being tracked purposefully
export const isLineItemSkadTracked = (
  //can be skan3- or skan4+
  campaign: ICampaignServer,
  lineItemId?: string
) => {
  const selectedLineItem = campaign.lineitems?.find(
    (li) => li.id === lineItemId
  );
  const isAppleBundle = isBundleIdOfApple(campaign?.bundleId);
  return selectedLineItem
    ? isAppleBundle && !!selectedLineItem.skAdNetworkConfiguration
    : isAppleBundle;
};

export const areSkadConfigsConsideredEqual = ({
  sc: sc1,
  scInitial: sc2,
}: {
  sc: ISkAdNetworkConfiguration;
  scInitial: ISkAdNetworkConfiguration;
}) => {
  if (!sc1 && !sc2) return true;
  if (!sc1 || !sc2) return false;

  const {
    skadBlockConfig: sc1SkadBlockConfig,
    createdAt: createdAt1,
    ...sc1Props
  } = sc1;
  const { blockMap: blockMap1, ...sc1SkadBlockConfigProps } =
    sc1SkadBlockConfig || {};

  const {
    skadBlockConfig: sc2SkadBlockConfig,
    createdAt: createdAt2,
    ...sc2Props
  } = sc2;
  const { blockMap: blockMap2, ...sc2SkadBlockConfigProps } =
    sc2SkadBlockConfig || {};

  return (
    isEqual(sc1Props, sc2Props) &&
    isEqual(sc1SkadBlockConfigProps, sc2SkadBlockConfigProps) &&
    (blockMap1?.length ?? 0) >= (blockMap2?.length ?? 0)
  );
};

export const OLD_TEMPLATE_PATH = "skAdNetworkConfiguration.templateId";
export const NEW_TEMPLATE_PATH =
  "skAdNetworkConfiguration.skadBlockConfig.templateType";
